import { CardContent } from '@mui/material';
import { CalendarDays, Clock } from 'lucide-react';
import React from 'react';
import { Badge, Card, CardHeader, CardTitle } from 'react-bootstrap';

const WarrantyDetails = ({ warrantyStarted, warrantyEnded, transactionId, claims, status }) => {
  const formatDate = (timestamp) => {
    if (!timestamp?.seconds) return "Invalid Date";
    return new Date(timestamp.seconds * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const statusMap = {
      'active': 'bg-green-500',
      'pending': 'bg-yellow-500',
      'approved': 'bg-green-500',
      'claimed': 'bg-blue-500'
    };
    return statusMap[status.toLowerCase()] || 'bg-gray-500';
  };

  const approvedClaims = claims?.filter(claim => claim.status === 'approved').length || 0;
  const pendingClaims = claims?.filter(claim => claim.status === 'pending').length || 0;

  return (
    <Card className="w-full bg-white px-10 py-7 rounded-xl">
      <CardHeader className="flex flex-row items-center justify-between border-b pb-4">
        <div>
          <CardTitle className="text-xl font-semibold">Warranty Details</CardTitle>
          <p className="text-sm text-gray-500">Transaction ID: {transactionId}</p>
        </div>
        <Badge className={`${getStatusColor(status)} text-white p-2 rounded-lg`}>
          {status}
        </Badge>
      </CardHeader>

      <CardContent className="space-y-6 pt-6">
        {/* Warranty Period */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium">Warranty Period</h3>
          <div className="flex flex-col sm:flex-row justify-between gap-4 bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">Started</p>
                <p className="font-medium">{formatDate(warrantyStarted)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">Ends</p>
                <p className="font-medium">{formatDate(warrantyEnded)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Claims Summary */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium">Claims Summary</h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-2xl font-semibold">{claims?.length || 0}</p>
              <p className="text-sm text-gray-500">Total Claims</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-2xl font-semibold text-green-600">{approvedClaims}</p>
              <p className="text-sm text-gray-500">Approved</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-2xl font-semibold text-yellow-600">{pendingClaims}</p>
              <p className="text-sm text-gray-500">Pending</p>
            </div>
          </div>
        </div>

        {/* Claims History */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium">Claims History</h3>
          <div className="space-y-4">
            {claims?.map((claim) => (
              <div key={claim.id} className="border rounded-lg p-4 space-y-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium">{claim.reason}</h4>
                    <p className="text-sm text-gray-500">{claim.reasonDescription}</p>
                  </div>
                  <Badge className={`${getStatusColor(claim.status)} text-white p-2 rounded-lg`}>
                    {claim.status}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Clock className="h-4 w-4" />
                  <span>Submitted on {formatDate(claim.claimedAt)}</span>
                </div>
                {claim.images && (
                  <div className="grid grid-cols-2 gap-4">
                    {claim.images.map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`Claim image ${index + 1}`}
                        className="w-full h-48 object-cover rounded-lg"
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default WarrantyDetails;